// Mobile app (Capacitor) device session: the app must never log the user out
// on its own. After login it gets a long-lived device key from the backend
// (POST /api/auth/device-session), keeps it in the Keychain / Keystore, and
// trades it for a fresh JWT (POST /api/auth/device-refresh) whenever the JWT
// expired: on start, when the app comes back to the foreground, and on a 401.
// Being offline never logs out; only a key the server refuses does.
// In the browser every function here is a no-op.
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { isNativeApp, nativePlatform } from './native';
import { shouldRefresh, readTokenTimes } from './sessionRefresh';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const KEY_NAME = 'runvya.deviceKey';

export async function getDeviceKey() {
  if (!isNativeApp()) return null;
  try {
    const { value } = await SecureStoragePlugin.get({ key: KEY_NAME });
    return value || null;
  } catch {
    return null; // no key stored
  }
}

async function saveDeviceKey(value) {
  await SecureStoragePlugin.set({ key: KEY_NAME, value });
}

async function clearDeviceKey() {
  try {
    await SecureStoragePlugin.remove({ key: KEY_NAME });
  } catch { /* nothing stored */ }
}

// After a successful login in the app: ask for a device key. Login still
// works without one (the session then simply expires like on the web).
export async function enrollDevice(accessToken) {
  if (!isNativeApp() || !accessToken) return false;
  try {
    const res = await fetch(`${API_URL}/api/auth/device-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ platform: nativePlatform(), device_name: navigator.userAgent.slice(0, 100) }),
    });
    if (!res.ok) return false;
    const { refresh_key: key } = await res.json();
    if (!key) return false;
    await saveDeviceKey(key);
    return true;
  } catch {
    return false;
  }
}

let inFlight = null;

// Trade the device key for a fresh JWT. Resolves to the new token, or null
// when there is no key, the server refused it (key dropped) or the network
// failed (key kept, try again later).
export function renewWithDeviceKey() {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    const key = await getDeviceKey();
    if (!key) return null;
    let res;
    try {
      res = await fetch(`${API_URL}/api/auth/device-refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_key: key }),
      });
    } catch {
      return null; // offline: keep the key and the stored session
    }
    if (res.status === 401 || res.status === 403) {
      await clearDeviceKey();
      return null;
    }
    if (!res.ok) return null;
    const data = await res.json();
    if (!data?.access_token) return null;
    localStorage.setItem('token', data.access_token);
    if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
    return data.access_token;
  })().finally(() => { inFlight = null; });
  return inFlight;
}

function isUsable(token, nowMs = Date.now()) {
  const times = token && readTokenTimes(token);
  return !!times && nowMs / 1000 < times.exp;
}

// Make sure the stored JWT is valid (renewing it with the device key when it
// is missing, expired or past half its life). Resolves to the token to use.
export async function ensureFreshSession() {
  let token = null;
  try {
    token = localStorage.getItem('token');
  } catch { /* storage unavailable */ }
  if (!isNativeApp()) return token;
  if (isUsable(token) && !shouldRefresh(token)) return token;
  const renewed = await renewWithDeviceKey();
  return renewed || token;
}

// User-initiated logout in the app: revoke the key on the server too.
export async function forgetDevice() {
  const key = await getDeviceKey();
  if (!key) return;
  await clearDeviceKey();
  try {
    await fetch(`${API_URL}/api/auth/device-logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_key: key }),
    });
  } catch { /* offline: the key is gone locally, it expires on the server */ }
}
